import { useState } from "react";
import { Link, Redirect } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormTitle, InputWithLabel, Layout, Logo } from "@ptsd/uikit";
import { ArrowLeft } from "@tamagui/lucide-icons";
import i18next from "i18next";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form, Image, Separator, Text, useMedia, View, XStack } from "tamagui";
import z from "zod";

import { api } from "~/utils/api";
import { authClient } from "~/utils/auth";
import { Footer, Header } from "~/components/layout";

export const schema = z.object({
  email: z
    .string()
    .nonempty(i18next.t("authValidationMessages:general.required"))
    .email(i18next.t("authValidationMessages:email.invalid-format")),
});

export type FormData = z.infer<typeof schema>;

//eslint-disable-next-line @typescript-eslint/no-var-requires
const img = require("../../assets/signup/signup-4.png");

//TODO: update i18n

const ForgotPassword = () => {
  const { sm } = useMedia();

  return (
    <Layout variant="dualbackground">
      {sm && <Header />}
      <View
        $sm={{
          flex: 1,
        }}
        flex={2}
        flexDirection="row"
        width={"100%"}
      >
        <View
          paddingHorizontal={"$2.5"}
          flex={1}
          alignItems="flex-end"
          width={"50%"}
          $gtSm={{
            paddingRight: 110,
          }}
          $sm={{
            alignItems: "center",
          }}
        >
          {!sm && (
            <View width={"100%"} paddingTop={"$5.8"} maxWidth={413}>
              <Logo
                style={{
                  width: 214,
                  height: 36,
                }}
              />
            </View>
          )}

          <ForgotPasswordFrom />
        </View>
        {!sm && (
          <View
            paddingHorizontal={"$2.5"}
            width={"50%"}
            alignItems="flex-start"
            paddingLeft={110}
            paddingTop={200}
            justifyContent="flex-start"
            flex={1}
          >
            <Image
              maxHeight={560}
              maxWidth={600}
              src={img}
              contain="size"
              width="100%"
              resizeMode="contain"
              aspectRatio={1}
            />
          </View>
        )}
      </View>
      {sm && <Footer />}
    </Layout>
  );
};

export default ForgotPassword;

const ForgotPasswordFrom = () => {
  const { sm } = useMedia();
  const { t } = useTranslation("forgotpassword");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [isAuthorized] = api.auth.isAuthorized.useSuspenseQuery(undefined);

  const { setError, control, handleSubmit, getValues, reset } =
    useForm<FormData>({
      defaultValues: {
        email: "",
      },
      resolver: zodResolver(schema),
    });

  const onSubmit = (data: z.infer<typeof schema>) => {
    if (isLoading || isSuccess) {
      return;
    }
    void (async () => {
      setIsSuccess(false);
      setIsLoading(true);
      const result = await authClient.requestPasswordReset({
        email: data.email,
        redirectTo: "/auth/reset-password",
      });

      if (result.error) {
        setError("email", {
          message: t("error.failed-to-send-email"),
        });
      }
      if (result.data?.status === true) {
        setIsSuccess(true);
      }
      setIsLoading(false);
    })();
  };

  if (isAuthorized) {
    return <Redirect href={"/"} />;
  }

  return (
    <Form
      width={"100%"}
      maxWidth={413}
      gap={"$3"}
      paddingVertical={"$15"}
      $sm={{
        paddingVertical: "$4",
      }}
      flex={1}
      justifyContent="flex-start"
      onSubmit={handleSubmit(onSubmit)}
    >
      <View width={"100%"} gap={"$5"}>
        {isSuccess && (
          <View gap={"$3"}>
            <View gap={"$2"}>
              <FormTitle>
                {t("success.title", { email: getValues("email") })}
              </FormTitle>

              <Text
                fontFamily={"$subtitle"}
                fontWeight={"$5"}
                fontSize={"$4"}
                lineHeight={"$4"}
              >
                {t("success.message")}
              </Text>
            </View>
            <Separator />
            <Button
              padding={0}
              height={"auto"}
              justifyContent="flex-start"
              variant={"transparent"}
              focusStyle={{
                backgroundColor: "transparent",
                borderWidth: 0,
              }}
              borderColor="transparent"
              hoverStyle={{
                backgroundColor: "transparent",
              }}
              pressStyle={{
                backgroundColor: "transparent",
              }}
              onPress={() => {
                reset();
                setIsSuccess((prev) => !prev);
              }}
            >
              <Text
                fontFamily={"$body"}
                fontWeight={"$5"}
                fontSize={"$3"}
                lineHeight={"$3"}
                color={"$lightblue-700"}
              >
                {t("success.did-not-receive-email")}
              </Text>
            </Button>
          </View>
        )}
        {!isSuccess && (
          <>
            <FormTitle>{t("form.title")}</FormTitle>
            <View width={"100%"} gap={"$2"}>
              <View width={"100%"} gap={"$4"}>
                <Controller
                  control={control}
                  name={"email"}
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState }) => {
                    return (
                      <InputWithLabel
                        data-id={"forgot-password-email"}
                        variant="inline"
                        size="md"
                        isError={!!fieldState.error?.message}
                        error={fieldState.error?.message}
                        label={t("form.email.label")}
                        placeholder={t("form.email.placeholder")}
                        onChangeText={(text) => field.onChange(text)}
                        {...field}
                      />
                    );
                  }}
                />
              </View>
            </View>
          </>
        )}
      </View>

      <XStack gap={"$0.5"}>
        <Link data-id={"forgot-password-back"} href="/" asChild>
          <Button
            variant="gray"
            size={sm ? "md" : "lg"}
            mode="icon"
            rounded="lg"
          >
            <ArrowLeft color={"$gray-primary-900"} size={"$2.5"} />
          </Button>
        </Link>

        {!isSuccess && (
          <Form.Trigger asChild>
            <Button
              data-id={"forgot-password-submit"}
              flex={1}
              variant="primary"
              size={sm ? "md" : "lg"}
              rounded="lg"
            >
              {isLoading ? t("form.submit.loading") : t("form.submit.label")}
            </Button>
          </Form.Trigger>
        )}
      </XStack>
    </Form>
  );
};
